import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { Bell, ChevronRight, Keyboard, Menu, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AccountMenu } from '@/app/layouts/AccountMenu';
import { NAV_ITEMS_BY_PATH, NAV_SECTION_BY_PATH } from '@/app/navigation';
import { cn } from '@/lib/cn';

// -----------------------------------------------------------------------------
// Top bar.
//
// It carries the PAGE TITLE. Screens used to open with a large `<h1>` and a
// paragraph beneath it, which meant the name of the section appeared three times
// above the data - once in the sidebar, once in the breadcrumb, once as the
// heading - and pushed the first table row an extra 120px down the page. The
// title lives here now, stated once, and the screens start at their content.
//
// The `<h1>` moved with it, so each route still has exactly one and the heading
// order below it stays sane for a screen reader.
//
// The bar carries three GLOBAL operations, and only operations that genuinely
// apply to every screen belong here - anything screen-specific lives in that
// screen's own section headers:
//
//   - Refresh, which invalidates the active queries. The spinner is driven by
//     `useIsFetching` so it reports the real state of the cache rather than a
//     local boolean that goes stale the moment a background refetch starts.
//   - Notifications, pointing at the dashboard: the one screen where
//     applications, reviews and tickets all surface together.
//   - The shortcut sheet, because a keyboard-first review queue is only
//     keyboard-first if the keys are discoverable from anywhere.
//
// There is deliberately no global search - no cross-entity search endpoint
// exists, and a box that only searches the current table is worse than none,
// because people type into it expecting global results.
// -----------------------------------------------------------------------------

export function Topbar({
  pendingCount,
  onOpenNav,
}: {
  pendingCount: number;
  onOpenNav: () => void;
}): JSX.Element {
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const item = NAV_ITEMS_BY_PATH[pathname];
  const sectionLabel = NAV_SECTION_BY_PATH[pathname];

  // Counts every in-flight query, including background refetches nobody
  // triggered. That is the point: the indicator answers "is this screen still
  // settling?", which is a different question from "did my click work?".
  const fetching = useIsFetching() > 0;

  return (
    <>
      <header className="flex h-topbar shrink-0 items-center gap-2 border-b border-border bg-background px-2.5 sm:gap-3 sm:px-4 lg:px-5">
        <Button
          variant="ghost"
          size="icon-sm"
          className="shrink-0 lg:hidden"
          onClick={onOpenNav}
          aria-label="Open navigation"
        >
          <Menu aria-hidden />
        </Button>

        <div className="min-w-0 flex-1">
          {/* The crumb is hidden below `sm` rather than truncated: on a phone
              a two-line block makes the bar the tallest thing on screen and
              pushes the content down for no information gain. */}
          {sectionLabel ? (
            <p className="hidden items-center gap-0.5 text-[11px] font-medium leading-none text-muted-foreground sm:flex">
              <span className="truncate">{sectionLabel}</span>
              <ChevronRight className="size-3 shrink-0 opacity-60" aria-hidden />
            </p>
          ) : null}

          <h1 className="truncate text-md font-semibold leading-tight text-foreground sm:mt-0.5">
            {item?.label ?? 'Not found'}
          </h1>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => void queryClient.refetchQueries({ type: 'active' })}
                aria-label={fetching ? 'Refreshing' : 'Refresh this screen'}
              >
                <RefreshCw className={cn(fetching && 'animate-spin')} aria-hidden />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{fetching ? 'Refreshing…' : 'Refresh this screen'}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className="hidden sm:inline-flex"
                onClick={() => setShortcutsOpen(true)}
                aria-label="Keyboard shortcuts"
              >
                <Keyboard aria-hidden />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Keyboard shortcuts</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon-sm" asChild className="relative">
                {/* Combined across applications, review queue and tickets, so it
                    cannot point at just one of them - the Dashboard is where all
                    three actually surface. Previously hardcoded to /applications,
                    which sent you to an empty queue whenever the count was really
                    a pending certificate or an open ticket. */}
                <Link
                  to="/"
                  aria-label={
                    pendingCount > 0
                      ? `${pendingCount} items awaiting review`
                      : 'Nothing awaiting review'
                  }
                >
                  <Bell aria-hidden />
                  {pendingCount > 0 ? (
                    <span
                      className="absolute right-1 top-1 flex size-[7px] rounded-full bg-destructive ring-2 ring-background"
                      aria-hidden
                    />
                  ) : null}
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {pendingCount > 0 ? `${pendingCount} awaiting review` : 'Nothing awaiting review'}
            </TooltipContent>
          </Tooltip>

          <span className="mx-0.5 h-5 w-px bg-border sm:mx-1" aria-hidden />

          <AccountMenu />
        </div>
      </header>

      <ShortcutSheet open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </>
  );
}

const SHORTCUTS: { group: string; keys: [string, string][] }[] = [
  {
    group: 'Review queue',
    keys: [
      ['J  /  ↓', 'Next item'],
      ['K  /  ↑', 'Previous item'],
      ['A', 'Approve the open item'],
      ['R', 'Reject the open item'],
    ],
  },
  {
    group: 'Anywhere',
    keys: [
      ['Esc', 'Close the open drawer or dialog'],
      ['Tab', 'Move through the controls in order'],
      ['Enter  /  Space', 'Open the focused table row'],
    ],
  },
];

/**
 * The shortcut reference.
 *
 * Lives in the top bar rather than on the review queue, because a shortcut only
 * gets learned if it can be looked up from wherever the operator happens to be
 * standing when they wonder about it.
 */
function ShortcutSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {SHORTCUTS.map((group) => (
            <div key={group.group}>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.11em] text-muted-foreground">
                {group.group}
              </p>
              <div className="overflow-hidden rounded border border-border">
                {group.keys.map(([key, description]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between gap-3 border-b border-border px-2.5 py-1.5 last:border-0"
                  >
                    <kbd className="shrink-0 rounded border border-input bg-muted px-1.5 py-0.5 font-mono text-xs text-secondary-foreground">
                      {key}
                    </kbd>
                    <span className="min-w-0 truncate text-sm text-muted-foreground">
                      {description}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
